import { Box, FormControl, Grid, Stack, Typography, useTheme } from "@mui/material";
import { FIELD_NUMBER_MAXVALUE, FIELD_NUMBER_MINVALUE } from "app/appConstants";
import { MprcLink } from "components";
import { MprcNumberField } from "components/MprcNumberField";
import { AssCorrectionResult, AssessmentInstanceDto, AssessmentMode, AssessmentPlayerMode, AssessmentQuestionDto, AssessmentType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import React from "react";
import { useTranslation } from "react-i18next";
import { useInstanceContext } from "views/assessment/player/AssPlayerBase";
import { AssRevisionResultIcon } from "views/assessment/player/common/AssCommon";
import { SurveyRevisionAnswersLine } from "views/assessment/player/common/SurveyRevisionAnswersLine";

/////////////////////////////////////////////////////////////////////////////////

interface IProps {
    viewData: AssessmentInstanceDto;
    question: AssessmentQuestionDto;
    onChange: (question: AssessmentQuestionDto) => void;
}

export const AssPlayerFillInNumber: React.FC<IProps> = ({ question, viewData, onChange }) => {

    const ctx = useInstanceContext();

    const { t } = useTranslation("assessment");
    const theme = useTheme();

    ///
    const changeAnswerNumber = (alternativeId: string, value: number | null) => {
        if (question) {
            var newAnswers = question.answers ? [...question.answers] : [];
            newAnswers = newAnswers.filter(x => x.alternativeId != alternativeId);
            newAnswers = [...newAnswers, {
                alternativeId: alternativeId,
                answer: value?.toString() ?? "",
                numericAnswer: value,
            }];
            onChange({ ...question, answers: newAnswers });
        }
    };

    ///
    const RenderAvailable = () => {
        return (
            <Stack direction="column" spacing={2}>
                {
                    question?.alternatives.map((v) => {
                        const answer = question.answers?.find(x => x.alternativeId == v.alternativeId)?.answer ?? "";
                        const numericAnswer = question.answers?.find(x => x.alternativeId == v.alternativeId)?.numericAnswer;
                        return (
                            <Stack key={v.alternativeId} direction="column">
                                <Grid container spacing={2} wrap="nowrap" alignItems="center">
                                    <Grid>
                                        <FormControl variant="outlined" sx={{ width: "110px" }}>
                                            <MprcNumberField
                                                allowNegative
                                                value={numericAnswer}
                                                variant="outlined"
                                                size="small"
                                                decimals={2}
                                                min={viewData.assessmentType == AssessmentType.Survey ? FIELD_NUMBER_MINVALUE : 0}
                                                max={FIELD_NUMBER_MAXVALUE}
                                                disabled={viewData.playerMode == AssessmentPlayerMode.UserReview || viewData.playerMode == AssessmentPlayerMode.FinalResult}
                                                sx={{ width: "110px" }}
                                                onChange={value => changeAnswerNumber(v.alternativeId, value)}
                                                error={viewData.assessmentType != AssessmentType.Evaluation && answer != "" &&
                                                    ((numericAnswer ?? 0) < question.fillInMinValue || (numericAnswer ?? 0) > question.fillInMaxValue)
                                                }
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid size="grow">
                                        <Typography variant="body1" component="div">
                                            {v.text}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Stack>
                        );
                    })
                }
            </Stack>
        );
    };

    ///
    const RenderEvalRevision = () => {
        return (
            <Stack direction="column" spacing={2}>
                {
                    question?.alternatives.map((v) => {
                        const numericAnswer = question.answers?.find(x => x.alternativeId == v.alternativeId)?.numericAnswer ?? 0;
                        const result = question.revision?.alternatives.find(x => x.alternativeId == v.alternativeId);
                        const highlightColor = result && result.correctionResult == AssCorrectionResult.Correct
                            ? theme.palette.success.main
                            : result && result.correctionResult == AssCorrectionResult.Wrong
                                ? theme.palette.error.main
                                : undefined;
                        const correctAnswer = !isNaN(Number(result?.correctAnswer)) ? Number(result?.correctAnswer) : undefined;
                        return (
                            <Stack key={v.alternativeId} direction="column">
                                <Grid container spacing={2} wrap="nowrap" alignItems="center">
                                    <Grid size={{ xs: 0.7 }} textAlign="center">
                                        <AssRevisionResultIcon
                                            result={result?.correctionResult}
                                            correctAnswer={
                                                correctAnswer
                                                    ? `${t("AssPlayer.Question.CorrectText")}: ${Formaters.number(correctAnswer, (correctAnswer % 1) == 0 ? 0 : 2)}`
                                                    : undefined
                                            }
                                        />
                                    </Grid>
                                    <Grid>
                                        <FormControl variant="outlined" sx={{ width: "110px" }}>
                                            <MprcNumberField
                                                value={numericAnswer}
                                                variant="outlined"
                                                size="small"
                                                allowNegative={viewData.assessmentType == AssessmentType.Survey}
                                                decimals={2}
                                                disabled
                                                sx={highlightColor
                                                    ?
                                                    {                                               // - The TextField-root
                                                        border: 'solid 2px #0ff',                   // - For demonstration: set the TextField-root border
                                                        borderColor: highlightColor,
                                                        borderRadius: "6px",
                                                        width: 100,

                                                        input: {
                                                            "-webkit-text-fill-color": `${highlightColor} !important`,
                                                            color: `${highlightColor} !important`,
                                                        },
                                                        // (Note: space or no space after `&` matters. See SASS "parent selector".)
                                                        '& .MuiOutlinedInput-root': {               // - The Input-root, inside the TextField-root
                                                            '& fieldset': {                         // - The <fieldset> inside the Input-root
                                                                borderColor: highlightColor,        // - Set the Input border
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: highlightColor,        // - Set the Input border when parent has :hover
                                                            },
                                                            '&.Mui-focused fieldset': {             // - Set the Input border when parent is focused
                                                                borderColor: highlightColor,
                                                            },
                                                            '& input': {                            // - The <fieldset> inside the Input-root
                                                                borderColor: highlightColor,        // - Set the Input border
                                                            },
                                                        },
                                                    }
                                                    :
                                                    { width: 100, }
                                                }
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid size="grow">
                                        <Typography variant="body1">
                                            {v.text}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Stack>
                        );
                    })
                }
            </Stack>
        );
    };

    ///
    const RenderSurvRevision = () => {
        return (
            <Box>

                <Typography variant="subtitle1" component="p">
                    {t("AssPlayer.Question.Revision.AverageResultsText")}
                </Typography>

                <Stack direction="column" mt={2} spacing={2}>
                    {
                        question?.alternatives.map((v) => {
                            const numericAnswer = question.answers?.find(x => x.alternativeId == v.alternativeId)?.numericAnswer ?? 0;
                            const result = question.revision?.alternatives.find(x => x.alternativeId == v.alternativeId);
                            const average = result?.averageResult ?? 0;

                            return (
                                <Stack key={v.alternativeId} direction="column">
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            sx={{ border: "1px solid grey", height: "28px", width: "120px" }}
                                        >
                                            <Typography variant="caption" color={result?.averageResult ?? 0 > 5 ? theme.palette.success.main : theme.palette.error.main}>
                                                {Formaters.number(average, (average % 1) == 0 ? 0 : 2)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="black">
                                            {
                                                question.revision?.identifyAnswers
                                                    ?
                                                    <MprcLink onClick={() => ctx.changeSelectedAlternative(v)}>
                                                        <Typography variant="caption">
                                                            {v.text}
                                                        </Typography>
                                                    </MprcLink>
                                                    :
                                                    <Typography variant="caption">
                                                        {v.text}
                                                    </Typography>
                                            }
                                            <>&nbsp;</>
                                            <Typography variant="caption" component="span" textTransform="lowercase" color={theme.palette.muted.main}>
                                                ({t("Literal.You", { ns: "shared" })}: {Formaters.number(numericAnswer, (numericAnswer % 1) == 0 ? 0 : 2)})
                                            </Typography>
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        })
                    }
                </Stack>
                <Box marginTop={2}>
                    {SurveyRevisionAnswersLine(viewData, question, t, ctx.deployId)}
                </Box>
            </Box>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {
                viewData.playerMode == AssessmentPlayerMode.FinalResult && viewData.mode == AssessmentMode.Survey
                    ? RenderSurvRevision()
                    : viewData.playerMode == AssessmentPlayerMode.UserReview && viewData.mode == AssessmentMode.Evaluation
                        ? RenderEvalRevision()
                        : RenderAvailable()
            }
        </>
    );
};
